import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import {
    useLocation,
    useNavigate,
    useParams
} from 'react-router-dom'
import styled, { css } from 'styled-components'

import * as Icons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import { createdUpdatedAt } from 'helpers/date/created-updated-at'
import { Icons as helpersIcons } from 'helpers/icons'
import {
    type ITicket
} from 'pages/support/pages/Tickets/types/interfaces/ITicket'
import { closeTicket } from 'pages/support/service/endpoints/tickets'
import { useDeleteTicket } from 'pages/support/service/react-query/useDeleteTicket/useDeleteTicket'
import { useGetTicketDetails } from 'pages/support/service/react-query/useGetTicketDetails/useGetTicketDetails'
import colors from 'styles/colors'

import { TicketReply } from './components/TicketReply'
import { TicketStatusZendesk } from '../../../types/enums/TicketStatus'
import {
    StyledAttachImage,
    StyledAttachInfo,
    StyledAttachText,
    StyledAttachmentsWrapper,
    StyledBottomArea,
    StyledBottomAreaBorder,
    StyledBottomAreaDetails,
    StyledBottomAreaDetailsLeft,
    StyledBottomAreaDetailsRight,
    StyledBottomAreaText,
    StyledDateDate,
    StyledDateText,
    StyledLabelNew,
    StyledMessageInfo,
    StyledMessageText,
    StyledStatus,
    StyledTicketIdInfo,
    StyledTicketIdText
} from '../common/TicketCard.styled'
import { TicketComment } from '../components/TicketComment/TicketComment'

interface TicketLocationState {
    ticket?: ITicket
}

export const TicketCardOpen = () => {
    const { setTitle } = usePageTitle()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()

    const { id } = useParams<{ id: string }>()

    /*
     * This comes directly from the ticket list.
     *
     * It allows us to render the main ticket immediately
     * without waiting for the detail request.
     */
    const locationState =
        location.state as TicketLocationState | null

    const initialTicket =
        locationState?.ticket ?? null

    const {
        data,
        isLoading,
        refetch
    } = useGetTicketDetails(id ?? '')

    const detailedTicket =
        data?.data?.message

    /*
     * Prefer the detailed response once available.
     *
     * Until then, use the exact ticket object from the list.
     */
    const ticket =
        detailedTicket ?? initialTicket

    const agentData =
        detailedTicket?.agent

    const comments =
        detailedTicket?.comments ?? []

    /*
     * While comments are loading, we already know roughly how many
     * reply cards should be present from the list response.
     */
    const expectedReplyCount =
        initialTicket?.comment_count ?? 0

    const scrollableRef =
        useRef<HTMLDivElement>(null)

    const { mutate: deleteTicketMutation } =
        useDeleteTicket()

    const zendeskTicketId =
        ticket?.zendesk_ticket_id ?? null

    const isSynced =
        Boolean(zendeskTicketId)

    const isPendingSync =
        ticket?.zendesk_sync_status === 'pending'

    const closeTicketMutation =
        useMutation(closeTicket, {
            onSuccess: async () => {
                await refetch()

                queryClient.invalidateQueries(
                    'tickets'
                )
            },

            onError: () => {
                addToast(
                    'error',
                    t('errorSomethingWentWrong')
                )
            }
        })

    const handleCloseTicket = () => {
        if (!ticket?.public_id) {
            return
        }

        closeTicketMutation.mutate(
            ticket.public_id
        )
    }

    const handleDeleteTicket = () => {
        if (!id) {
            return
        }

        deleteTicketMutation(id, {
            onSettled: () => {
                navigate(
                    '/support/submit-ticket'
                )

                queryClient.invalidateQueries(
                    'tickets'
                )
            },

            onError: () => {
                addToast(
                    'error',
                    t('errorSomethingWentWrong')
                )
            }
        })
    }

    useEffect(() => {
        setTitle(t('support'))
    }, [
        setTitle,
        t
    ])

    useEffect(() => {
        if (!isPendingSync) {
            return
        }

        const interval =
            window.setInterval(() => {
                void refetch()
            }, 2500)

        return () => {
            window.clearInterval(interval)
        }
    }, [
        isPendingSync,
        refetch
    ])

    /*
     * Direct URL load:
     *
     * If somebody refreshes /support/ticket/{id}, there is no router
     * state. In that case we have to wait for the request.
     *
     * The support shell still renders immediately.
     */
    if (!ticket && !isLoading) {
        return (
            <>
                <StyledWrapper>
                    <StyledEmptyTicket>
                        {t('noTickets')}
                    </StyledEmptyTicket>
                </StyledWrapper>

                <DesktopAdditionalContent />
            </>
        )
    }

    const displayStatus =
        isPendingSync
            ? t('waitingForSupport')
            : ticket?.zendesk_sync_status ===
                'failed'
              ? t('submissionFailed')
              : ticket?.status

    const displayTicketId =
        ticket?.zendesk_ticket_id ??
        ticket?.public_id

    const firstCommentAttachments =
        comments?.[0]?.attachments ?? []

    const canReply =
        ticket &&
        ticket.status !==
            TicketStatusZendesk.CLOSED &&
        ticket.status !==
            TicketStatusZendesk.SOLVED

    return (
        <>
            <StyledWrapper ref={scrollableRef}>
                {ticket ? (
                    <ActionCard
                        key={
                            ticket.public_id ??
                            ticket.id
                        }
                        customStyle={css`
                            margin-top: 2rem;
                        `}
                        icon={
                            helpersIcons.document
                        }
                        iconSize='small'
                        text={
                            <>
                                {t(
                                    ticket.subject
                                )}

                                {ticket.status ===
                                    TicketStatusZendesk.PENDING &&
                                    !isPendingSync && (
                                        <StyledLabelNew>
                                            {t(
                                                'new'
                                            )}
                                        </StyledLabelNew>
                                    )}
                            </>
                        }
                        subtext={
                            <StyledDateText>
                                {t('created')}

                                <StyledDateDate>
                                    {createdUpdatedAt(
                                        ticket.created_at
                                    )}
                                </StyledDateDate>
                            </StyledDateText>
                        }
                        trashFn={
                            handleDeleteTicket
                        }
                        {...(isSynced && {
                            hasCheckmarkFn:
                                handleCloseTicket
                        })}
                        bottomArea={
                            <StyledBottomArea>
                                <StyledBottomAreaText>
                                    {
                                        ticket.description
                                    }
                                </StyledBottomAreaText>

                                <StyledBottomAreaBorder />

                                <StyledBottomAreaDetails>
                                    <StyledBottomAreaDetailsLeft>
                                        {(ticket.attachments_count ??
                                            firstCommentAttachments.length) >
                                            0 && (
                                            <StyledAttachInfo>
                                                <Icons.IconAttach />

                                                <StyledAttachText>
                                                    {ticket.attachments_count ??
                                                        firstCommentAttachments.length}
                                                </StyledAttachText>
                                            </StyledAttachInfo>
                                        )}

                                        {(ticket.comment_count ??
                                            detailedTicket?.comment_count ??
                                            comments.length) >
                                            0 && (
                                            <StyledMessageInfo>
                                                <Icons.IconChatSmall />

                                                <StyledMessageText>
                                                    {ticket.comment_count ??
                                                        detailedTicket?.comment_count ??
                                                        comments.length}
                                                </StyledMessageText>
                                            </StyledMessageInfo>
                                        )}
                                    </StyledBottomAreaDetailsLeft>

                                    <StyledBottomAreaDetailsRight>
                                        <StyledTicketIdInfo>
                                            <StyledTicketIdText>
                                                {t(
                                                    'ticket'
                                                )}
                                            </StyledTicketIdText>

                                            {displayTicketId
                                                ? `#${displayTicketId}`
                                                : ''}
                                        </StyledTicketIdInfo>

                                        <StyledStatus
                                            $isOpen={
                                                isPendingSync ||
                                                ticket.status ===
                                                    TicketStatusZendesk.OPEN
                                            }
                                        >
                                            {
                                                displayStatus
                                            }
                                        </StyledStatus>
                                    </StyledBottomAreaDetailsRight>
                                </StyledBottomAreaDetails>

                                <StyledAttachmentsWrapper>
                                    {firstCommentAttachments.length >
                                    0 ? (
                                        <div>
                                            {firstCommentAttachments.map(
                                                attachment => (
                                                    <StyledAttachImage
                                                        key={
                                                            attachment.id
                                                        }
                                                        src={
                                                            attachment.content_url
                                                        }
                                                    />
                                                )
                                            )}
                                        </div>
                                    ) : null}
                                </StyledAttachmentsWrapper>
                            </StyledBottomArea>
                        }
                    />
                ) : (
                    <TicketMainSkeleton />
                )}

                {isLoading ? (
                    <>
                        {Array.from({
                            length:
                                expectedReplyCount
                        }).map(
                            (_, index) => (
                                <TicketCommentSkeleton
                                    key={`ticket-comment-skeleton-${index}`}
                                />
                            )
                        )}
                    </>
                ) : (
                    comments.map(comment => (
                        <TicketComment
                            key={comment.id}
                            ticketId={id ?? ''}
                            comment={comment}
                            agentData={
                                agentData
                            }
                        />
                    ))
                )}

                {canReply &&
                ticket?.public_id ? (
                    <StyledReplyArea>
                        <TicketReply
                            ticketId={
                                ticket.public_id
                            }
                        />
                    </StyledReplyArea>
                ) : null}
            </StyledWrapper>

            <DesktopAdditionalContent />
        </>
    )
}

/*
 * Used only for direct links/refreshes where we do not have
 * a ticket passed from the list.
 */
const TicketMainSkeleton = () => {
    return (
        <StyledMainSkeleton>
            <StyledSkeletonTop>
                <StyledSkeletonIcon />

                <StyledSkeletonTitleArea>
                    <StyledSkeletonTitle />
                    <StyledSkeletonDate />
                </StyledSkeletonTitleArea>

                <StyledSkeletonAction />
                <StyledSkeletonAction />
            </StyledSkeletonTop>

            <StyledSkeletonMessage />

            <StyledSkeletonDivider />

            <StyledSkeletonBottom>
                <StyledSkeletonSmall />
                <StyledSkeletonRight />
            </StyledSkeletonBottom>
        </StyledMainSkeleton>
    )
}

/*
 * This uses ActionCard itself so its outside dimensions,
 * padding, radius and responsive behavior match TicketComment.
 */
const TicketCommentSkeleton = () => {
    return (
        <ActionCard
            customStyle={css`
                margin-top: 2rem;
            `}
            icon={
                <StyledSkeletonAvatar />
            }
            text={
                <StyledSkeletonCommentName />
            }
            subtext={
                <StyledSkeletonCommentDate />
            }
            bottomArea={
                <StyledSkeletonCommentBody>
                    <StyledSkeletonCommentLineLong />
                    <StyledSkeletonCommentLineMedium />
                </StyledSkeletonCommentBody>
            }
        />
    )
}

const StyledWrapper = styled.div`
    background: ${colors.white90deg};
    padding: 2rem 2rem 10rem;
`

const StyledReplyArea = styled.div`
    width: 100%;
    margin-top: 2rem;
`

const StyledEmptyTicket = styled.div`
    width: 100%;
    padding: 4rem 2rem;
    text-align: center;
    font-size: 1.6rem;
    font-weight: 500;
    opacity: 0.4;
`

/*
 * Same skeleton appearance as the other skeletons in the app:
 *
 * #ebebeb base
 * soft white shimmer
 * 1.5 second animation
 */
const skeletonCss = css`
    position: relative;
    overflow: hidden;
    background: #ebebeb;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        transform: translateX(-100%);

        background-image: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.45) 50%,
            rgba(255, 255, 255, 0) 100%
        );

        animation:
            ticketSkeletonShimmer
            1.5s
            ease-in-out
            infinite;
    }

    @keyframes ticketSkeletonShimmer {
        100% {
            transform: translateX(100%);
        }
    }
`

const StyledSkeletonAvatar = styled.div`
    ${skeletonCss};

    width: 4.8rem;
    height: 4.8rem;
    border-radius: 50%;
`

const StyledSkeletonCommentName = styled.div`
    ${skeletonCss};

    width: 9rem;
    height: 1.6rem;
    border-radius: 999px;
`

const StyledSkeletonCommentDate = styled.div`
    ${skeletonCss};

    width: 7rem;
    height: 1.1rem;
    border-radius: 999px;
    margin-top: 0.6rem;
`

const StyledSkeletonCommentBody = styled.div`
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
`

const StyledSkeletonCommentLineLong = styled.div`
    ${skeletonCss};

    width: 88%;
    height: 1.4rem;
    border-radius: 999px;
    margin-bottom: 1rem;
`

const StyledSkeletonCommentLineMedium = styled.div`
    ${skeletonCss};

    width: 55%;
    height: 1.4rem;
    border-radius: 999px;
`

const StyledMainSkeleton = styled.div`
    margin-top: 2rem;
    padding: 2.5rem;
    border-radius: 2rem;
    background: ${colors.white};
`

const StyledSkeletonTop = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;
`

const StyledSkeletonIcon = styled.div`
    ${skeletonCss};

    width: 5rem;
    height: 5rem;
    border-radius: 1.5rem;
    flex: 0 0 auto;
`

const StyledSkeletonTitleArea = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.8rem;
`

const StyledSkeletonTitle = styled.div`
    ${skeletonCss};

    width: 14rem;
    height: 1.7rem;
    border-radius: 999px;
`

const StyledSkeletonDate = styled.div`
    ${skeletonCss};

    width: 10rem;
    height: 1.2rem;
    border-radius: 999px;
`

const StyledSkeletonAction = styled.div`
    ${skeletonCss};

    width: 4.8rem;
    height: 4.8rem;
    border-radius: 1.2rem;
`

const StyledSkeletonMessage = styled.div`
    ${skeletonCss};

    width: 45%;
    height: 1.5rem;
    border-radius: 999px;
    margin-top: 3rem;
`

const StyledSkeletonDivider = styled.div`
    height: 1px;
    background: #ebebeb;
    margin: 3rem 0 2rem;
`

const StyledSkeletonBottom = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const StyledSkeletonSmall = styled.div`
    ${skeletonCss};

    width: 4rem;
    height: 1.3rem;
    border-radius: 999px;
`

const StyledSkeletonRight = styled.div`
    ${skeletonCss};

    width: 11rem;
    height: 1.3rem;
    border-radius: 999px;
`